import { Button } from '@bambi/component-library/src/components/Button/Button';
import { Combobox } from '@bambi/component-library/src/components/inputs/Combobox/Combobox';
import { Select } from '@bambi/component-library/src/components/inputs/Select/Select';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { useEffect, useMemo, useState } from 'react';

import { Modal } from './primitives/Modal';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { authSlice } from 'src/features/auth/auth.slice';
import { setSendbirdAccessToken } from 'src/features/chat/chat.slice';

export function ChangeOrganizationModal() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.profile);
  const currentOrganizationId = useAppSelector(
    (state) => state.auth.currentOrganizationId
  );
  const isOrganizationChangeOpen = useAppSelector(
    (state) => state.auth.isOrganizationChangeOpen
  );

  const [newOrganizationSelection, setNewOrganizationSelection] = useState(
    currentOrganizationId
  );
  const [organizationOptions, setOrganizationOptions] = useState<
    SelectOption[]
  >([]);

  useEffect(() => {
    setOrganizationOptions(
      user?.memberships.map((membership) => {
        return {
          label: membership.organization.name,
          value: membership.organization.id || '',
        };
      }) || []
    );
  }, [user]);

  useEffect(() => {
    setNewOrganizationSelection(currentOrganizationId);
  }, [currentOrganizationId]);

  const selectedOrganization = useMemo(() => {
    return organizationOptions.find(
      (opt) => opt.value === newOrganizationSelection
    );
  }, [organizationOptions, newOrganizationSelection]);

  const onOrganizationChange = () => {
    dispatch(authSlice.actions.changeOrganization(newOrganizationSelection));
    dispatch(
      setSendbirdAccessToken({
        membershipId: '',
        sendbirdAccessToken: '',
      })
    );
    dispatch(authSlice.actions.setIsOrganizationChangeOpen(false));

    // TODO: Rework how we handle Sendbird.
    // Removing this causes the app to blow up when GroupChannelListHeader tries to read
    // from a null user. This happens for a few reasons:
    // 1. We end up with two or more Sendbird instances
    // 2. The one inside GroupChannelListHeader has a null user that's typed as always defined
    // 3. GroupChannelListHeader is rerendered before the chat component is unmounted
    // Apparently this happens not only on /chat, but anywhere in the app followed by opening chat.
    // Reloading as a workaround to lessen complaints from the web app side.
    // Leaving the rest of the
    // eslint-disable-next-line no-restricted-globals
    window.location.reload();

    // All our data is tied to the organization id with few exceptions
    // This invalidates and refetches with the new org id
    // This is no longer needed since we're reloading above, but important when
    // we figure out Sendbird
    // dispatch(enhancedBambiApi.util.resetApiState());
  };

  return (
    <Modal
      open={isOrganizationChangeOpen}
      setOpen={() => {
        dispatch(authSlice.actions.setIsOrganizationChangeOpen(false));
      }}
      title="Which organization would you like to use?"
    >
      <div className="flex flex-col gap-2">
        <h2 className="text-md font-bold">
          Which organization would you like to use?
        </h2>
        {organizationOptions.length > 5 ? (
          <Combobox
            options={organizationOptions}
            value={selectedOrganization}
            onChange={(newValue) => {
              setNewOrganizationSelection(newValue.value);
            }}
            placeholder="Select an organization"
            dataTestId="organization-combobox"
          />
        ) : (
          <Select
            options={organizationOptions}
            value={selectedOrganization?.value || ''}
            onChange={(newValue) => {
              setNewOrganizationSelection(newValue?.value || '');
            }}
          />
        )}
        <Button
          variant="primary"
          onClick={() => {
            onOrganizationChange();
          }}
        >
          Change Current Organization
        </Button>
      </div>
    </Modal>
  );
}
